import { useState } from 'react';
import { Sparkle, Warning, X } from '@phosphor-icons/react';
import { useActiveDemoLicenseCount } from '@/lib/licenses/useIssueDemoLicense';
import { useEmployees } from '@/lib/employees/useEmployees';
import { useCreateDemoEndClient } from '@/lib/end-clients/useCreateDemoEndClient';
import type { WizardDemoMode } from '@/components/sessions/ScheduleSessionWizard';

const DURATION_PRESETS = [
  { label: '90 days', days: 90 },
  { label: '180 days', days: 180 },
  { label: '365 days', days: 365 },
];

export interface LaunchedDemoConfig {
  endClientId: string;
  demoMode: WizardDemoMode;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called when the demo end_client has been created and the wizard should
   *  open. The parent is responsible for unmounting this launcher and
   *  rendering <ScheduleSessionWizard demoMode={...} defaultEndClientId={...}>. */
  onLaunch: (cfg: LaunchedDemoConfig) => void;
}

export function IssueDemoLauncher({ open, onClose, onLaunch }: Props) {
  const { data: capInfo } = useActiveDemoLicenseCount();
  const { data: employees, isLoading: empLoading } = useEmployees();
  const createDemo = useCreateDemoEndClient();

  const [employeeId, setEmployeeId] = useState<string>('');
  const [candidateName, setCandidateName] = useState<string>('Sample Candidate');
  const [days, setDays] = useState<number>(365);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const atCap = capInfo ? capInfo.count >= capInfo.cap : false;
  const employee = (employees ?? []).find((e) => e.id === employeeId);

  async function handleLaunch() {
    setError(null);

    if (!employeeId || !employee) {
      setError('Pick a recipient employee.');
      return;
    }
    if (!candidateName.trim()) {
      setError('Demo candidate name is required.');
      return;
    }
    if (!employee.email) {
      setError('Recipient employee has no email; pick another.');
      return;
    }

    try {
      const demoEc = await createDemo.mutateAsync({ candidateName });
      const recipientLabel = employee.full_name?.trim() || employee.email;
      onLaunch({
        endClientId: demoEc.id,
        demoMode: {
          validUntilDays: days,
          assignedToEmployeeId: employee.id,
          assignedToEmployeeEmail: employee.email,
          recipientLabel,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create demo candidate.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="issue-demo-launcher"
        className="glass-card w-full max-w-md p-6 space-y-5"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkle weight="fill" className="h-5 w-5 text-orange-400" />
              <h2 className="text-lg font-semibold text-white">Issue Demo License</h2>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Spin up a demo candidate and walk through the same NeuraX experience your real
              candidates get.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-muted-foreground hover:bg-white/5 hover:text-white"
            aria-label="Close"
            disabled={createDemo.isPending}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="rounded-xl border border-orange-500/30 bg-orange-500/10 px-3 py-2.5 text-xs text-orange-300/90">
          Demos don&apos;t consume your monthly quota or generate billing records.
          {capInfo && (
            <span className={`block mt-1 font-medium ${atCap ? 'text-red-300' : ''}`}>
              {capInfo.count} of {capInfo.cap} demo licenses active.
            </span>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Recipient employee
          </label>
          <select
            data-testid="demo-employee-select"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            disabled={empLoading || createDemo.isPending}
            className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white"
          >
            <option value="">— Pick an employee —</option>
            {(employees ?? []).map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.full_name || emp.email} {emp.role === 'admin' ? '(admin)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Demo candidate name
          </label>
          <input
            data-testid="demo-candidate-name-input"
            type="text"
            value={candidateName}
            onChange={(e) => setCandidateName(e.target.value)}
            placeholder="Sample Candidate"
            disabled={createDemo.isPending}
            className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white placeholder:text-muted-foreground/60"
          />
          <p className="text-[11px] text-muted-foreground/80">
            Shown in the wizard and on the desktop app. Anything works — &ldquo;Acme Demo&rdquo;,
            &ldquo;Sample Candidate&rdquo;, etc.
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Expires after
          </label>
          <div className="flex gap-2">
            {DURATION_PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                onClick={() => setDays(p.days)}
                disabled={createDemo.isPending}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm transition ${
                  days === p.days
                    ? 'border-neurax-electric/60 bg-neurax-electric/15 text-white'
                    : 'border-white/10 bg-white/[0.02] text-muted-foreground hover:bg-white/5'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <Warning className="mt-0.5 h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={createDemo.isPending}
            className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-2 text-sm text-muted-foreground hover:bg-white/5 hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="launch-demo-btn"
            onClick={handleLaunch}
            disabled={createDemo.isPending || atCap}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createDemo.isPending ? 'Creating demo…' : 'Continue → upload resume & JD'}
          </button>
        </div>
      </div>
    </div>
  );
}
